import React, { useState } from 'react';
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap';
import Select from '../../components/form/AppSelect';
import '../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useQuery, useMutation } from '@tanstack/react-query';

/**
 * AddBundleModal — pick a bundle and add its items to a project or rental.
 *
 * Props:
 *   show          {boolean}
 *   onHide        {function}
 *   onAddItems    {function(entries: Array<{stock_item_id, item_name, quantity}>)}
 *                 Called once with the full list of entries when user confirms.
 */
const AddBundleModal = ({ show, onHide, onAddItems }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('expand'); // 'expand' | 'preset'
  const [error, setError] = useState(null);

  const { data: bundles = [], isLoading: loading } = useQuery({
    queryKey: ['item-bundles'],
    queryFn: async () => {
      const response = await api.get('warehouses/api/item-bundles/');
      return response.results || response || [];
    },
    enabled: show,
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const entries = selectedBundle.entries.map(entry => ({
        stock_item_id: entry.stock_item,
        item_name: entry.item_name,
        item_obj: {
          id: entry.stock_item,
          name: entry.item_name,
          quantity_available: entry.item_quantity_available,
          color: entry.item_color,
          stock_name: entry.item_stock_name,
        },
        quantity: entry.quantity,
        notes: entry.notes,
      }));
      await onAddItems(entries, mode);
    },
    onSuccess: () => { onHide(); },
    onError: () => { setError(t('warehouses.bundles.addError')); },
  });
  const submitting = confirmMutation.isPending;

  const bundleOptions = bundles.map(b => ({
    value: b.id,
    label: `${b.name} (${t(`warehouses.bundles.${b.bundle_type}`)}) — ${b.entry_count} ${t('warehouses.bundles.entryCount')}`,
    bundle: b,
  }));

  const selectedBundle = selected?.bundle ?? null;

  const handleConfirm = () => {
    if (!selectedBundle || !selectedBundle.entries || selectedBundle.entries.length === 0) return;
    confirmMutation.mutate();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('warehouses.bundles.addToProject')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <CenteredLoader />
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>{t('warehouses.bundles.selectBundle')}</Form.Label>
              <Select
                options={bundleOptions}
                value={selected}
                onChange={opt => setSelected(opt)}
                isClearable
                placeholder={t('warehouses.bundles.selectBundle')}
                classNamePrefix="react-select"
              />
            </Form.Group>

            {selectedBundle && (
              <>
                <div className="mb-3">
                  <Form.Label>{t('warehouses.bundles.bundlePreview')}</Form.Label>
                  {selectedBundle.entries && selectedBundle.entries.length > 0 ? (
                    <Table size="sm" bordered className="mb-0">
                      <thead>
                        <tr>
                          <th>{t('warehouses.bundles.item')}</th>
                          <th style={{ width: '80px' }}>{t('warehouses.bundles.quantity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBundle.entries.map(entry => (
                          <tr key={entry.id}>
                            <td>
                              {entry.item_name}
                              {entry.item_stock_name && (
                                <span className="text-muted small ms-1">({entry.item_stock_name})</span>
                              )}
                            </td>
                            <td>{entry.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="text-muted small mb-0">{t('warehouses.bundles.noEntries')}</p>
                  )}
                </div>

                <Form.Group>
                  <Form.Label>{t('warehouses.bundles.bundleType')}</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      id="mode-expand"
                      label={t('warehouses.bundles.expandIntoItems')}
                      value="expand"
                      checked={mode === 'expand'}
                      onChange={() => setMode('expand')}
                      className="mb-1"
                    />
                    <Form.Check
                      type="radio"
                      id="mode-preset"
                      label={t('warehouses.bundles.addAsBundle')}
                      value="preset"
                      checked={mode === 'preset'}
                      onChange={() => setMode('preset')}
                    />
                  </div>
                </Form.Group>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          {t('warehouses.scanner.close')}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selectedBundle || submitting || loading}
        >
          {submitting ? '…' : t('warehouses.bundles.addToProject')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddBundleModal;
